import { useEffect, useState } from 'react';

import { CONNECT_CODE_ALPHABET } from '@/crypto';
import { useT } from '@/i18n';
import { formatConnectCode } from '@/lib/format';
import { connectLink } from '@/lib/links';
import styles from './Share.module.css';
import shared from '@/pages/pages.module.css';

import { Button } from './Button';
import { CheckIcon, CopyIcon } from './Icons';
import { EmojiRow } from './Nearby';
import type { ShareCodePanelProps } from './ShareCodePanel.types';

/** Letters the shuffling cells pick from while a code is on its way (the code's own alphabet look). */
const SHUFFLE_MS = 90;

/**
 * The code's six cells while the code is on its way: faint letters shuffle in
 * them, as if the code were being worked out, then the real one takes their
 * place. With reduced motion the cells simply wait, empty. The letters are
 * decoration, hidden from screen readers.
 */
function ShufflingCells(): React.JSX.Element {
  const [tick, setTick] = useState(0);
  const still = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  useEffect(() => {
    if (still) return undefined;
    const handle = window.setInterval(() => {
      setTick((t) => t + 1);
    }, SHUFFLE_MS);
    return () => {
      window.clearInterval(handle);
    };
  }, [still]);
  const letter = (i: number): string => (still ? '' : (CONNECT_CODE_ALPHABET[(tick * 7 + i * 11 + ((tick * (i + 3)) % 5)) % CONNECT_CODE_ALPHABET.length] ?? ''));
  return (
    <>
      {[0, 1, 2, -1, 3, 4, 5].map((i) =>
        i < 0 ? (
          <span key="dash" className={styles.codeDash} aria-hidden="true" />
        ) : (
          <span key={i} className={[styles.codeCell, styles.codeCellLoading].join(' ')} aria-hidden="true">
            {letter(i)}
          </span>
        ),
      )}
    </>
  );
}

/** The Code tab: the owner's confirmation of a device that typed the code, or the address, the live code and its timer. */
export function ShareCodePanel({ connect, viewsLeft, copiedAddress, onCopyAddress, footerConfirm = false, confirming = false }: ShareCodePanelProps): React.JSX.Element {
  const t = useT();
  const { code, codeError, connected, pending, secondsLeft, progress, startCode, allow, refuse } = connect;
  return (
    <div id="share-panel-code" role="tabpanel" aria-labelledby="share-tab-code" className={[styles.sharePanel, styles.sharePanelCenter].join(' ')}>
      {pending !== null ? (
        <div role="alertdialog" aria-labelledby="connect-confirm-title" className={styles.connectConfirm} data-testid="connect-confirm">
          <p id="connect-confirm-title" className={styles.shareLead}>
            {t('shareCode.entered', { device: pending.device })}
          </p>
          <p className={styles.shareWarn}>{t('shareCode.matchPrompt')}</p>
          <EmojiRow emoji={pending.emoji} testId="connect-emoji" />
          {pending.sameNetwork ? null : (
            <p className={shared.fieldError} role="alert" data-testid="connect-other-network">
              {t('shareCode.otherNetwork')}
            </p>
          )}
          <div className={styles.row}>
            {footerConfirm ? null : (
              <Button onClick={() => void allow()} data-testid="connect-allow">
                {t('common.match')}
              </Button>
            )}
            <Button variant="secondary" onClick={refuse} disabled={confirming} data-testid="connect-refuse">
              {t('shareCode.notMe')}
            </Button>
          </div>
        </div>
      ) : connected ? (
        <>
          <p className={styles.shareLead}>{t('shareCode.connected')}</p>
          <p className={styles.shareStatus}>
            <CheckIcon size={18} /> {t('shareCode.connectedNote')}
          </p>
        </>
      ) : viewsLeft <= 0 ? (
        <p className={styles.shareLead} role="alert">
          {t('share.noViewsLeft')}
        </p>
      ) : (
        <>
          <p className={styles.stepLabel}>
            <span className={styles.stepNum}>1</span>{t('shareCode.step1')}
          </p>
          <div className={[styles.linkBox, copiedAddress ? styles.linkBoxCopied : ''].join(' ')}>
            <span className={styles.linkText} title={connectLink()} data-ltr="" data-testid="connect-url">
              {connectLink().replace(/^https?:\/\//, '')}
            </span>
            <button type="button" className={[styles.linkCopyBtn, copiedAddress ? styles.linkCopied : ''].join(' ')} onClick={() => { onCopyAddress(); }} aria-label={copiedAddress ? t('common.copied') : t('shareCode.copyAddress')} title={t('shareCode.copyAddress')}>
              {copiedAddress ? <CheckIcon size={20} /> : <CopyIcon size={20} />}
            </button>
          </div>
          <p className={styles.stepLabel}>
            <span className={styles.stepNum}>2</span>{t('shareCode.step2')}
          </p>
          <div className={styles.codeCells} aria-live="polite" aria-busy={code === null} data-testid="connect-code-cells">
            {code
              ? formatConnectCode(code.code)
                  .split('')
                  .map((ch, i) => (ch === '-' ? <span key={i} className={styles.codeDash} aria-hidden="true" /> : <span key={i} className={styles.codeCell}>{ch}</span>))
              : <ShufflingCells />}
          </div>
          <span className="visually-hidden" data-testid="connect-code">
            {code ? formatConnectCode(code.code) : ''}
          </span>
          <p className={styles.codeMeta}>
            {code ? t('shareCode.changesIn', { time: `${String(Math.floor(secondsLeft / 60))}:${String(secondsLeft % 60).padStart(2, '0')}` }) : t('shareCode.generating')}
          </p>
          <div className={styles.shareBar} role="progressbar" aria-label={t('shareCode.timerLabel')} aria-valuemin={0} aria-valuemax={60} aria-valuenow={secondsLeft}>
            <i style={{ width: `${String(progress)}%` }} />
          </div>
          {codeError ? (
            <p className={shared.fieldError} role="alert">
              {codeError}{' '}
              <button type="button" className={styles.linkBtn} onClick={() => void startCode()}>
                {t('common.tryAgain')}
              </button>
            </p>
          ) : (
            <p className={styles.shareWarn}>{t('shareCode.keepOpen')}</p>
          )}
        </>
      )}
    </div>
  );
}
